import React, { Dispatch, ReactElement, SetStateAction } from 'react'

import { Box, Button, Grid, Typography } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { GameType } from 'constants/enums/GameTypeEnum'

import arrowDown from 'assets/images/arrowDown.svg'
import arrowUp from 'assets/images/arrowUp.svg'

import { styleProps, styles } from './GeneratedExampleDropdown.styles'
import { generatedExampleDropdownData } from './generatedExampleDropdownData'

export type Props = {
	currentResponses?: string[]
	customResponse?: ReactElement
	gameTypeEnum: GameType
	headerText: string
	isLoading?: boolean
	setShowAIExampleIdea: Dispatch<SetStateAction<boolean>>
	showAIExampleIdea: boolean
}

const TEST_ID = 'generated-example-dropdown'

export const GeneratedExampleDropdown = ({
	gameTypeEnum,
	headerText,
	currentResponses,
	customResponse,
	isLoading,
	showAIExampleIdea,
	setShowAIExampleIdea,
}: Props) => {
	const gameId = gameTypeEnum.value as number

	const gameHeroData = generatedExampleDropdownData[gameId]

	const loadingElement = () => (
		<Grid sx={styles.responseContainer(gameHeroData.bgPrimary)}>
			<Box sx={styles.LoadingSpinnerBox}>
				<CircularProgress sx={styles.loadingSpinner} />
			</Box>
		</Grid>
	)

	const buildResponseItem = (solution: string[]) => {
		return (
			<ol
				data-testid="gen-ai-sub-response-list"
				style={styles.responseList(gameHeroData.bgSecondary)}
			>
				{solution.map((sol: string, idx: number) => (
					<Typography
						component="li"
						key={`ai-sub-solution-${idx}`}
						sx={styles.solutionText}
						{...styleProps.responseText}
					>
						{sol}
					</Typography>
				))}
			</ol>
		)
	}

	const renderContent = () => {
		if (isLoading) {
			return loadingElement()
		}

		if ((currentResponses?.length ?? 0) > 0 && !customResponse) {
			return buildResponseItem(currentResponses!)
		}

		return customResponse
	}

	return (
		<Box>
			<Typography>{headerText}</Typography>
			<Box sx={styles.buttonContainer(gameHeroData.bgSecondary)}>
				<Button
					{...styleProps.button}
					data-testid={`${TEST_ID}-show-ai-example-button`}
					onClick={() => setShowAIExampleIdea(!showAIExampleIdea)}
					sx={styles.aiButton}
				>
					<Box sx={styles.buttonContent}>
						<Typography sx={styles.typography}>
							View AI-Generated Example Ideas
						</Typography>
						<img
							alt="arrow"
							data-testid={`${TEST_ID}-arrow`}
							src={
								(showAIExampleIdea
									? arrowUp
									: arrowDown) as unknown as string
							}
						/>
					</Box>
				</Button>
			</Box>

			{showAIExampleIdea && renderContent()}
		</Box>
	)
}
